import { useState, useEffect, useCallback } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getFirestore, doc, getDoc } from "firebase/firestore";

/**
 * Custom hook for checking team permissions with improved pro feature access
 * @param {string} teamId - The team ID to check permissions for
 * @returns {Object} Permission state and helper functions
 */
const useTeamPermissions = (teamId) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [role, setRole] = useState("unknown");
  const [isTeamPro, setIsTeamPro] = useState(false);
  const [permissions, setPermissions] = useState({});
  const [user, setUser] = useState(null);
  const [teamData, setTeamData] = useState(null);
  const [userIsPro, setUserIsPro] = useState(false);

  const auth = getAuth();
  const db = getFirestore();

  // Auth state observer
  useEffect(() => {
    console.log("Setting up auth observer with teamId:", teamId);
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      console.log("Auth state changed", currentUser ? currentUser.email : "no user");
      setUser(currentUser);
      
      if (!currentUser) {
        setRole("unknown");
        setPermissions({});
        setError("Authentication error: Please log in again");
        setLoading(false);
      }
    });
    
    return () => unsubscribe();
  }, [auth]);

  // Function to check user's pro status based on subscription
  const checkUserProStatus = useCallback(async (userId) => {
    if (!userId) return false;
    
    try {
      const userRef = doc(db, "users", userId);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        
        // Check various possible ways pro status might be stored
        if (userData.pro === true || userData.isPro === true) {
          return true;
        }
        
        // Check if there's a subscription field/object
        if (userData.subscription) {
          const subscription = userData.subscription;
          // Check if plan is 'pro' or similar
          if (subscription.plan && 
             (subscription.plan.toLowerCase() === 'pro' || 
              subscription.plan.toLowerCase().includes('pro'))) {
            return true;
          }
          
          // Check if subscription is active and not expired
          if (subscription.expiresAt && 
              new Date(subscription.expiresAt.toDate()) > new Date()) {
            return subscription.plan && 
                  (subscription.plan.toLowerCase() === 'pro' || 
                   subscription.plan.toLowerCase().includes('pro'));
          }
        }
      }
      return false;
    } catch (err) {
      console.error("Error checking pro status:", err);
      return false;
    }
  }, [db]);

  // Load team data and check permissions
  const loadTeamData = useCallback(async () => {
    if (!user || !teamId) return;
    
    try {
      setLoading(true);
      
      // Check if current user is pro
      const isPro = await checkUserProStatus(user.uid);
      setUserIsPro(isPro);
      console.log("Current user pro status:", isPro);
      
      // Load team data
      const teamRef = doc(db, "teams", teamId);
      const teamSnap = await getDoc(teamRef);
      
      if (!teamSnap.exists()) {
        setError("Team not found");
        setLoading(false);
        return;
      }
      
      const data = teamSnap.data();
      setTeamData(data);
      
      // Determine team pro status
      let teamIsPro = false;
      
      // Check if team has direct pro status
      if (data.isPro === true || data.hasProSubscription === true) {
        teamIsPro = true;
      } 
      // Check if team creator/admin is pro
      else if (data.coachId) {
        const coachIsPro = await checkUserProStatus(data.coachId);
        if (coachIsPro) {
          teamIsPro = true;
        } else if (data.admins && data.admins.length > 0) {
          // Check the first admin as fallback
          const adminIsPro = await checkUserProStatus(data.admins[0]);
          teamIsPro = adminIsPro;
        }
      }
      
      setIsTeamPro(teamIsPro);
      console.log("Team pro status:", teamIsPro);
      
      // Determine role
      let userRole = "unknown";
      if (data.coachId === user.uid) {
        userRole = "admin";
      } else if (data.admins && data.admins.includes(user.uid)) {
        userRole = "admin";
      } else if (data.members && data.members.includes(user.uid)) {
        userRole = "member";
      }
      
      setRole(userRole);
      console.log("User role for team:", userRole);
      
      // Set permissions based on role and pro status
      const allPermissions = {
        // Basic permissions anyone on team gets
        view_team: true,
        view_roster: true,
        view_schedule: true,
        view_games: true,
        
        // Admin/coach only permissions
        edit_team: userRole === "admin",
        manage_roster: userRole === "admin",
        manage_practices: userRole === "admin",
        manage_games: userRole === "admin",
        
        // Pro features - available if team is pro or user is pro
        view_player_stats: teamIsPro || isPro,
        edit_player_stats: userRole === "admin" && (teamIsPro || isPro),
        view_stats: teamIsPro || isPro,
        advanced_stats: teamIsPro || isPro,
        add_media: teamIsPro || isPro,
        manage_billing: userRole === "admin"
      };
      
      console.log("Permissions set:", allPermissions);
      setPermissions(allPermissions);
      setError(null);
    } catch (err) {
      console.error("Error loading team data:", err);
      setError("Error checking permissions: " + err.message);
    } finally {
      setLoading(false);
    }
  }, [user, teamId, db, checkUserProStatus]);

  // Check permissions when user or teamId changes
  useEffect(() => {
    if (user && teamId) {
      loadTeamData();
    }
  }, [user, teamId, loadTeamData]);

  // Function to check if a specific permission is granted
  const hasPermission = useCallback((action) => {
    if (loading || error) return false;
    
    // For debugging
    console.log(`Checking permission: ${action}`, permissions[action]);
    
    return permissions[action] === true;
  }, [loading, error, permissions]);

  // Role check utility functions
  const isAdmin = useCallback(() => role === "admin", [role]);
  const isCoach = useCallback(() => role === "admin", [role]); // Treat coach same as admin
  const isMember = useCallback(() => role === "member", [role]);

  // Function to refresh data
  const refreshPermissions = useCallback(() => {
    loadTeamData();
  }, [loadTeamData]);

  // Function to refresh the auth token
  const refreshToken = useCallback(async () => {
    if (!user) return false;
    
    try {
      console.log("Refreshing token for user", user.email);
      await user.getIdToken(true);
      return true;
    } catch (err) {
      console.error("Error refreshing token:", err);
      setError("Failed to refresh authentication token. Please log out and back in.");
      return false;
    }
  }, [user]);

  // Function to force re-login
  const forceRelogin = useCallback(() => {
    auth.signOut().then(() => {
      window.location.href = "/login";
    }).catch(err => {
      console.error("Error signing out:", err);
    });
  }, [auth]);

  return {
    loading,
    error,
    role,
    isTeamPro,
    hasPermission,
    isAdmin,
    isCoach,
    isMember,
    refreshPermissions,
    refreshToken,
    forceRelogin,
    userAuthenticated: !!user,
    userIsPro,
    teamData
  };
};

export default useTeamPermissions;